import * as React from 'react'

import { Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

/**
 * The label above a group of nav rows — `PERSONAL` / `COMPANY` in the settings nav,
 * `ACCOUNTS` / `COMMERCE` / `OPERATIONS` in the internal sidebar.
 *
 * ## Why this is shared rather than eight CSS properties copied twice
 *
 * The settings nav hand-rolled it inline (`SettingsPage.tsx`, the `<Typography variant="body4">`
 * inside the section map). The internal sidebar is the SECOND site that needs the same
 * treatment, which is exactly the trigger working-conventions §36 names: one hand-roll is
 * allowed, the second is a shared component. Left as two copies, the pair drifts silently —
 * §31 records four field components that each claimed "matches the shared Input" in a COMMENT
 * and stopped matching the moment the token moved.
 *
 * ## ⚠️ ABSOLUTE PIXELS, NOT `theme.spacing()`
 *
 * This is a layer-1 (`@repo/ui`) component, so it renders under whichever app's theme mounts
 * it — and the two apps have DIFFERENT spacing bases (`@repo/ui` 4px, `apps/app` MUI's default
 * 8px, working-conventions §29/§47). A `theme.spacing(2)` here would be 8px in the platform and
 * 16px in the app, and unlike a per-call-site `spacing={n}` there is no "convert per app"
 * available from inside the component. So every gap below is a literal px value.
 *
 * ## The two densities
 *
 * `default` reproduces the settings nav byte for byte, so migrating that call site is a
 * no-op rather than a re-layout. It is deliberately loose: 20px above each heading is what
 * separates the groups there, because that nav has no other boundary marker.
 *
 * `compact` is for a rail that is already tight. The sidebar's internal menu is 12 rows in a
 * 249px box at 1280x720, so four settings-density headings (26px for the first, 46px for each
 * subsequent one — 72px for the settings nav's two, measured) would have been ~118px of new
 * chrome. Compact trades the 20px separation gap for 8px, which it can afford because the
 * sidebar's rows carry their own 4px padding and the heading is binding to the group BELOW it
 * rather than floating between two.
 */
export interface NavSectionLabelProps {
  children: React.ReactNode
  /**
   * `default` = the settings nav's spacing (20px above, 8px below).
   * `compact` = a tight rail (8px above, 4px below), for the sidebar.
   */
  density?: 'default' | 'compact'
  /** First label in its list — drops the leading gap, which would otherwise indent the group from the top. */
  first?: boolean
  /**
   * Keep the BOX, drop the ink and the a11y announcement.
   *
   * Not cosmetic. The settings nav relies on the heading's box as a spacer so the first
   * interactive row sits at the same y whether or not a label renders — see the post-mortem
   * in `SettingsPage.tsx`, where removing the element instead pulled the first row's rounded
   * corner under the sticky page-title band by 4.5px. `visibility: hidden` also drops it from
   * the a11y tree, so no screen reader announces a group label that is not shown.
   */
  hidden?: boolean
  sx?: SxProps<Theme>
}

const DENSITY = {
  default: { pt: 20, pb: 8 },
  compact: { pt: 8, pb: 4 },
} as const

export const NavSectionLabel = ({
  children,
  density = 'default',
  first = false,
  hidden = false,
  sx,
}: NavSectionLabelProps) => {
  const { pt, pb } = DENSITY[density]

  return (
    <Typography
      color="text.secondary"
      component="div"
      sx={[
        {
          display: 'block',
          // Every type property is set explicitly rather than inherited from
          // `variant="body4"`, which the settings nav uses. packages/ui compiles WITHOUT the
          // app's MUI Typography module augmentation, so `body3`/`body4` are not assignable
          // here — `FieldLabel` hit the identical wall and records the same reasoning.
          // Nothing is lost: the settings heading overrides fontSize, weight, tracking and
          // case anyway, so the variant was only ever supplying a line-height, which is
          // stated below.
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          // 12px — the settings nav's own row inset, so the label clears the rail's edge.
          //
          // ⚠️ This is the CONTAINER's inset, NOT where a row's content starts. It read
          // "lines up with the text of the rows it labels" and that was never true of
          // either call site: the settings nav's row text sits at 364 against a heading at
          // 324, and the internal sidebar's at 64 against the same 12. In the sidebar the
          // gap was visible enough to be reported — a heading 16px left of every icon under
          // it — so a caller whose rows carry ICONS must override this with the row's
          // content start (`SIDEBAR_ROW_CONTENT_LEFT` there). Left as a default rather than
          // made required because the settings nav's 4px offset is imperceptible at 11px
          // and changing it is a separate, unasked-for re-layout.
          paddingLeft: '12px',
          paddingTop: first ? 0 : `${pt}px`,
          paddingBottom: `${pb}px`,
          // `default` restates body4's own `18px` so the settings nav is reproduced
          // exactly. `compact` takes 1.4 (~15px at 11px), because 18px of leading is sized
          // for running text and this is a one-word all-caps label — 3px per heading, times
          // three headings, in a rail measured in single pixels.
          lineHeight: density === 'compact' ? 1.4 : '18px',
          ...(hidden ? { visibility: 'hidden' } : {}),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Typography>
  )
}
